/**
 * [AI補完仕様] このコメントはAI補完（コード自動生成）に構造情報として渡されます。変更時は実装品質に直結するため正確に保つこと。
 *
 * TanStack Query v5 フック — BusinessTrip の一覧取得・作成・更新・削除・遷移操作を提供する。
 *
 * @contract useQuery/useMutation を使うこと。コンポーネント内で fetch を直接呼ばないこと。
 * @contract データ変更後は queryClient.invalidateQueries で一覧を再取得すること。
 * @contract このファイルは QueryClientProvider 配下のコンポーネントから呼ぶこと（main.tsx で設定済み）。
 * @signature useBusinessTrips(view?: string, filters?: Record<string, unknown>): UseQueryResult<{ data: any[]; total: number }>
 * @signature useBusinessTrip(id: string): UseQueryResult
 * @signature useCreateBusinessTrip(): UseMutationResult
 * @signature useUpdateBusinessTrip(): UseMutationResult
 * @signature useDeleteBusinessTrip(): UseMutationResult
 * @signature useTransitionBusinessTrip(): UseMutationResult
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../lib/api-client';

export function useBusinessTrips(view?: string, filters?: Record<string, unknown>) {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['businesstrips', view, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (view) params.append('view', String(view));
      if (filters) {
        Object.entries(filters).forEach(([k, v]) => {
          if (v === undefined || v === null) return;
          if (Array.isArray(v)) {
            v.forEach((item) => params.append(k, String(item)));
          } else {
            params.append(k, String(v));
          }
        });
      }
      const qs = params.toString();
      const url = qs ? `/businesstrips?${qs}` : '/businesstrips';
      const res = await apiClient.get(url);
      const data = res.data.data ?? res.data;
      return { data, total: res.data.total ?? (Array.isArray(data) ? data.length : 0) };
    },
  });
}

export function useBusinessTrip(id: string) {
  const apiClient = useApiClient();
  return useQuery({
    queryKey: ['businesstrips', id],
    queryFn: async () => {
      const res = await apiClient.get('/businesstrips/' + id);
      return res.data.data ?? res.data;
    },
    enabled: !!id,
  });
}

export function useCreateBusinessTrip() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiClient.post('/businesstrips', data);
      if (!res.data.success) throw new Error(res.data.error?.message ?? 'Create failed');
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesstrips'] });
    },
  });
}

export function useUpdateBusinessTrip() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await apiClient.put('/businesstrips/' + id, data);
      if (!res.data.success) throw new Error(res.data.error?.message ?? 'Update failed');
      return res.data.data;
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['businesstrips'] });
      queryClient.invalidateQueries({ queryKey: ['businesstrips', id] });
    },
  });
}

export function useDeleteBusinessTrip() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete('/businesstrips/' + id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesstrips'] });
    },
  });
}

export function useTransitionBusinessTrip() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: string }) => {
      const res = await apiClient.post('/businesstrips/' + id + '/transition', { action });
      if (!res.data.success) throw new Error(res.data.error?.message ?? 'Transition failed');
      return res.data.data;
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['businesstrips'] });
      queryClient.invalidateQueries({ queryKey: ['businesstrips', id] });
    },
  });
}